import { useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { DayPicker } from 'react-day-picker';
import { es, enUS } from 'react-day-picker/locale';
import { Calendar, Clock, X } from 'lucide-react';
import 'react-day-picker/style.css';
import { cn } from '@/lib/cn';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { useT } from '@/lib/i18n/useT';

export type DatePickerMode = 'date' | 'datetime';

interface DatePickerProps {
  /**
   * Selected value, in the SAME string protocol as the native inputs this
   * replaces:
   *   - `mode="date"`     → `"YYYY-MM-DD"`
   *   - `mode="datetime"` → `"YYYY-MM-DDTHH:mm"` (local time, no zone)
   * Empty string means "no value".
   */
  value: string;
  onChange: (value: string) => void;
  /** `date` = calendar only (projects); `datetime` = calendar + time (events). */
  mode?: DatePickerMode;
  /** Lower bound, same string format as `value`. Days before it are disabled. */
  min?: string;
  /** Upper bound, same string format as `value`. Days after it are disabled. */
  max?: string;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  /** Show a clear (×) affordance when a value is set. */
  clearable?: boolean;
  id?: string;
  className?: string;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const TIME_RE = /T(\d{2}):(\d{2})/;

/** Parse our string protocol into a local Date (or null). */
function parseValue(value: string): Date | null {
  const dm = DATE_RE.exec(value);
  if (!dm) return null;
  const [, y, mo, d] = dm;
  const tm = TIME_RE.exec(value);
  const hh = tm ? Number(tm[1]) : 0;
  const mm = tm ? Number(tm[2]) : 0;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), hh, mm, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Date + mode → our string protocol. */
function formatValue(date: Date, mode: DatePickerMode): string {
  const base = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  if (mode === 'date') return base;
  return `${base}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** "HH:mm" from a value string, defaulting to "09:00". */
function timeOf(value: string): string {
  const tm = TIME_RE.exec(value);
  return tm ? `${tm[1]}:${tm[2]}` : '09:00';
}

/** Strip a value's time component to midnight (for day-only comparisons). */
function dayStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Los extremos del desplegable de años cuando el campo no tiene límites.
 *
 * Hacia atrás poco —dos años— porque casi nada se programa en el pasado y un
 * desplegable con veinte años obliga a buscar. Hacia delante cinco, que cubre
 * cualquier campaña que alguien esté planificando de verdad.
 */
const aniosAtras = (n: number) => new Date(new Date().getFullYear() - n, 0, 1);
const aniosAdelante = (n: number) => new Date(new Date().getFullYear() + n, 11, 31);

/**
 * Cross-browser date / date-time picker. A drop-in replacement for the native
 * `<input type="date">` / `<input type="datetime-local">` — same string
 * protocol in/out — but with a real calendar (react-day-picker) and a separate
 * `<input type="time">` so the time is always reachable (Firefox hides it on
 * `datetime-local`). Themed entirely with `--color-xk-*` tokens, bilingual.
 */
export function DatePicker({
  value,
  onChange,
  mode = 'datetime',
  min,
  max,
  disabled,
  invalid,
  placeholder,
  clearable = true,
  id,
  className,
}: DatePickerProps) {
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  const [open, setOpen] = useState(false);

  const selected = useMemo(() => parseValue(value), [value]);
  const minDate = useMemo(() => (min ? parseValue(min) : null), [min]);
  const maxDate = useMemo(() => (max ? parseValue(max) : null), [max]);

  const disabledMatcher = useMemo(() => {
    const matchers: ({ before: Date } | { after: Date })[] = [];
    if (minDate) matchers.push({ before: dayStart(minDate) });
    if (maxDate) matchers.push({ after: dayStart(maxDate) });
    return matchers.length ? matchers : undefined;
  }, [minDate, maxDate]);

  const label = useMemo(() => {
    if (!selected) return placeholder ?? t('datePicker.placeholder');
    const datePart = new Intl.DateTimeFormat(language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(selected);
    if (mode === 'date') return datePart;
    return `${datePart} · ${timeOf(value)}`;
  }, [selected, mode, value, placeholder, language, t]);

  const commitDay = (day: Date | undefined) => {
    if (!day) return;
    if (mode === 'date') {
      onChange(formatValue(day, 'date'));
      setOpen(false);
      return;
    }
    // Preserve the current time-of-day (or the default) when switching days.
    const [hh, mm] = timeOf(value).split(':').map(Number);
    const next = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hh, mm);
    onChange(formatValue(next, 'datetime'));
  };

  const commitTime = (hhmm: string) => {
    if (!hhmm) return;
    const [hh, mm] = hhmm.split(':').map(Number);
    const base = selected ?? new Date();
    const next = new Date(base.getFullYear(), base.getMonth(), base.getDate(), hh, mm);
    onChange(formatValue(next, 'datetime'));
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <Popover.Root open={open} onOpenChange={disabled ? undefined : setOpen}>
      <div className={cn('relative', className)}>
        <Popover.Trigger asChild>
          <button
            type="button"
            id={id}
            disabled={disabled}
            data-empty={!selected}
            aria-invalid={invalid || undefined}
            className={cn(
              'flex h-10 w-full items-center gap-2 rounded-lg border bg-[var(--color-bg-input)] px-3 text-left text-sm transition-colors',
              'border-[var(--color-border-default)] hover:border-[var(--color-border-strong)] focus:border-[var(--color-accent-ink)] focus:outline-none',
              invalid && 'border-[var(--color-error)] focus:border-[var(--color-error)]',
              disabled && 'cursor-not-allowed opacity-50',
              clearable && selected && !disabled && 'pr-9',
            )}
          >
            <Calendar className="h-4 w-4 shrink-0 text-[var(--color-xk-text-muted)]" />
            <span
              className={cn(
                'flex-1 truncate',
                selected ? 'text-[var(--color-fg-primary)]' : 'text-[var(--color-fg-subtle)]',
              )}
            >
              {label}
            </span>
          </button>
        </Popover.Trigger>
        {clearable && selected && !disabled && (
          <button
            type="button"
            aria-label={t('datePicker.clear')}
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-[var(--color-xk-text-muted)] hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-fg-primary)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] p-3 shadow-xl"
        >
          <div
            style={
              {
                '--rdp-accent-color': 'var(--color-xk-accent)',
                '--rdp-accent-background-color': 'var(--color-xk-accent)',
                '--rdp-today-color': 'var(--color-xk-accent)',
                '--rdp-day-width': '2.25rem',
                '--rdp-day-height': '2.25rem',
                color: 'var(--color-fg-primary)',
                fontSize: '0.8125rem',
              } as React.CSSProperties
            }
          >
            <DayPicker
              mode="single"
              selected={selected ?? undefined}
              onSelect={commitDay}
              defaultMonth={selected ?? minDate ?? undefined}
              disabled={disabledMatcher}
              /*
               * Mes y año como desplegables, no sólo flechas.
               *
               * El calendario abre en el mes de hoy, así que programar algo
               * para diciembre eran cuatro clics en la flecha antes de poder
               * elegir el día — y para el año que viene, doce. Con los
               * desplegables se salta de un clic, y las flechas siguen ahí
               * para el caso normal, que es moverse un mes.
               */
              captionLayout="dropdown"
              /*
               * El rango de años del desplegable.
               *
               * Cuando el campo tiene límites (un evento dentro de su
               * proyecto) mandan ellos, y de paso siguen impidiendo navegar
               * fuera. Sin límites hace falta un rango explícito igualmente:
               * sin él el desplegable de años sale con un puñado de años
               * alrededor de hoy y no llega a donde el cliente quiere ir.
               */
              startMonth={minDate ?? aniosAtras(2)}
              endMonth={maxDate ?? aniosAdelante(5)}
              locale={language === 'es' ? es : enUS}
              showOutsideDays
            />
          </div>
          {mode === 'datetime' && (
            <div className="mt-2 flex items-center gap-2 border-t border-[var(--color-border-default)] pt-3">
              <Clock className="h-4 w-4 text-[var(--color-xk-text-muted)]" />
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
                {t('datePicker.time')}
              </span>
              <input
                type="time"
                value={timeOf(value)}
                onChange={(e) => commitTime(e.target.value)}
                className="ml-auto h-9 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] px-2 text-sm text-[var(--color-fg-primary)] focus:border-[var(--color-accent-ink)] focus:outline-none"
              />
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
